import React from 'react';
import './ProcessTemplateCard.css';
import useRoleSettings from '../../hooks/useRoleSettings';
import { colorVar, normalizeColorKey } from '../../util/participantGroups';
import { FLAT_RATE_KIND, flatRateKindIcon, flatRateKindLabel, isDeduction, recipientLabel } from '../../util/flatRates';
import {
  STEP_KIND_ICON, STEP_KIND_LABEL, TEMPLATE_RULES, normalizeStepKind, normalizeTargetKind, usageSummary,
} from './templateModel';

// Tooltip of a step in the chain: "Freigabe · Mitglieder, Gäste".
function stepTitle(step, groupsById) {
  const kind = normalizeStepKind(step.kind);
  const targets = (step.targets ?? []).map(t => (normalizeTargetKind(t.targetKind) === 'Newsletter'
    ? 'Newsletter'
    : (groupsById?.get(Number(t.participantGroupId))?.pluralName ?? '')))
    .filter(Boolean);
  return targets.length > 0 ? `${STEP_KIND_LABEL[kind]} · ${targets.join(', ')}` : STEP_KIND_LABEL[kind];
}

// Tooltip of the flat-rate line: one "Mailverarbeitung → Timo Klosok" per flat rate, one "Feldmiete (Abzug)"
// per deduction (names come with the list).
const flatRatesTitle = (flatRates) => flatRates
  .map(f => (isDeduction(f)
    ? `${f.flatRateName || 'Abzug'} (Abzug)`
    : `${f.flatRateName || 'Pauschale'} → ${recipientLabel(f)}`))
  .join('\n');

// "2 Pauschalen · 1 Abzug" — zero parts left out.
function flatRatesLine(flatRates) {
  const deductions = flatRates.filter(isDeduction).length;
  const counts = [[FLAT_RATE_KIND.flatRate, flatRates.length - deductions], [FLAT_RATE_KIND.deduction, deductions]];
  return counts
    .filter(([, count]) => count > 0)
    .map(([kind, count]) => `${count} ${flatRateKindLabel(kind, { plural: count !== 1 })}`)
    .join(' · ');
}

// "Leihausrüstung, BBs" — names of the sign-up add-ons (come with the list).
const addOnsLine = (addOns) => addOns.map(a => a.addOnName || 'Zusatz').join(', ');

/**
 * One template in the /processtemplates list: icon tile in the template color, event label, rule
 * badges, sign-up add-ons, number of standard flat rates and deductions, step chain, usage and the actions
 * Bearbeiten / Duplizieren / Archivieren|Wiederherstellen.
 */
function ProcessTemplateCard({ template, groupsById, onEdit, onDuplicate, onArchive, onRestore, disabled = false, disabledReason }) {
  useRoleSettings(); // role names in the flat-rate tooltip
  const colorKey = normalizeColorKey(template.colorKey);
  const flatRates = template.flatRates ?? [];
  const addOns = template.addOns ?? [];
  // Icon of the line: Pauschale while the template has one, else Abzug.
  const flatRatesIcon = flatRateKindIcon(flatRates.every(isDeduction) ? FLAT_RATE_KIND.deduction : FLAT_RATE_KIND.flatRate);
  const badges = TEMPLATE_RULES.filter(r => r.badge && template[r.key]);
  const steps = [...(template.steps ?? [])].sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0));
  const usage = usageSummary(template.usage);
  const unused = usage === 'Nicht genutzt';

  return (
    <li
      className={`ptCard${template.isArchived ? ' isArchived' : ''}${colorKey === 'neutral' ? ' isNeutral' : ''}`}
      style={{ '--pt-card-color': colorVar(colorKey) }}
    >
      <div className="ptCardHead">
        <span className="material-symbols-rounded ptCardIcon" aria-hidden="true">{template.icon || 'account_tree'}</span>
        <span className="ptCardTitle">
          <span className="ptCardName">{template.name}</span>
          <span className="ptCardEventLabel">Events heißen „{template.eventLabel}“</span>
        </span>
        {template.isArchived && <span className="ptCardArchived">Archiviert</span>}
      </div>

      {template.description && <p className="ptCardDescription">{template.description}</p>}

      {badges.length > 0 && (
        <ul className="ptCardBadges" aria-label="Regeln">
          {badges.map(r => (
            <li key={r.key} className="ptCardBadge" title={r.hint}>
              <span className="material-symbols-rounded" aria-hidden="true">{r.icon}</span>
              {r.badge}
            </li>
          ))}
        </ul>
      )}

      {addOns.length > 0 && (
        <p className="ptCardFlatRates ptCardAddOns" title={`Zusätze: ${addOnsLine(addOns)}`}>
          <span className="material-symbols-rounded" aria-hidden="true">inventory_2</span>
          <span className="ptCardAddOnsText">{addOnsLine(addOns)}</span>
        </p>
      )}

      {flatRates.length > 0 && (
        <p className="ptCardFlatRates" title={flatRatesTitle(flatRates)}>
          <span className="material-symbols-rounded" aria-hidden="true">{flatRatesIcon}</span>
          {flatRatesLine(flatRates)}
        </p>
      )}

      {steps.length > 0 ? (
        <ol className="ptCardSteps" aria-label="Schritte">
          {steps.map((s, i) => (
            <li key={s.id ?? i} className="ptCardStep" title={stepTitle(s, groupsById)}>
              <span className="material-symbols-rounded" aria-hidden="true">{s.icon || STEP_KIND_ICON[normalizeStepKind(s.kind)]}</span>
              <span className="ptCardStepLabel">{s.label}</span>
              {i < steps.length - 1 && <span className="material-symbols-rounded ptCardStepArrow" aria-hidden="true">chevron_right</span>}
            </li>
          ))}
        </ol>
      ) : (
        <p className="ptCardNoSteps">Keine Prozessschritte – Events werden nur angelegt.</p>
      )}

      <div className="ptCardFooter">
        <span className={`ptCardUsage${unused ? ' isUnused' : ''}`}>{usage}</span>
        <span className="ptCardActions">
          <button
            type="button"
            className="ptCardBtn isLabelled"
            onClick={() => onEdit?.(template)}
            disabled={disabled}
            title={disabled ? disabledReason : undefined}
          >
            <span className="material-symbols-rounded" aria-hidden="true">edit</span>
            Bearbeiten
          </button>
          <button
            type="button"
            className="ptCardBtn"
            onClick={() => onDuplicate?.(template)}
            disabled={disabled}
            aria-label={`${template.name} duplizieren`}
            title={disabled ? disabledReason : 'Duplizieren (legt eine Kopie an)'}
          >
            <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
          </button>
          {template.isArchived ? (
            <button
              type="button"
              className="ptCardBtn"
              onClick={() => onRestore?.(template)}
              disabled={disabled}
              aria-label={`${template.name} wiederherstellen`}
              title={disabled ? disabledReason : 'Wiederherstellen'}
            >
              <span className="material-symbols-rounded" aria-hidden="true">unarchive</span>
            </button>
          ) : (
            <button
              type="button"
              className="ptCardBtn isDanger"
              onClick={() => onArchive?.(template)}
              disabled={disabled}
              aria-label={`${template.name} archivieren`}
              title={disabled ? disabledReason : 'Archivieren'}
            >
              <span className="material-symbols-rounded" aria-hidden="true">archive</span>
            </button>
          )}
        </span>
      </div>
    </li>
  );
}

export default ProcessTemplateCard;
